import React, { useMemo, useState, useEffect } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useUser } from '../../../contexts/UserContext';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import './UserToggleSettings.scss';
import SunIcon from '../../../assets/icons/svg/sunon.svg';
import DarkIcon from '../../../assets/icons/svg/darkon.svg';
import SystemIcon from '../../../assets/icons/svg/systemon.svg';
import LogoutIcon from '../../../assets/icons/svg/logout.svg';
import SettingsIcon from '../../../assets/icons/svg/settings.svg';
import userService from '../../../services/userService';
import fileService from '../../../services/fileService';
import AvatarSection from '../../modals/add-item/components/AvatarSection';

const UserToggleSettings = ({ isOpen, onClose }) => {
  const { currentTheme, setTheme } = useTheme();
  const { user, clearUser, refreshUser, setUser } = useUser();
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm();

  const displayName = useMemo(() => {
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    return user?.name || user?.email || 'User';
  }, [user]);

  const displayEmail = useMemo(() => {
    return user?.email || 'user@example.com';
  }, [user]);

  // Load avatar URL when user changes
  useEffect(() => {
    const loadAvatar = async () => {
      if (user?.profileImage) {
        // If it's already a full URL (signed URL or blob), use it directly
        if (user.profileImage.startsWith('http://') || user.profileImage.startsWith('https://') || user.profileImage.startsWith('blob:')) {
          setAvatarUrl(user.profileImage);
        } else {
          // Get signed URL for S3 key
          try {
            const signedUrl = await fileService.getSignedUrl(user.profileImage, 3600);
            setAvatarUrl(signedUrl || user.profileImage);
          } catch (error) {
            console.error('Error loading avatar:', error);
            setAvatarUrl(null);
          }
        }
      } else {
        setAvatarUrl(null);
      }
    };
    loadAvatar();
  }, [user?.profileImage]);

  // Close avatar menu when clicking outside
  useEffect(() => {
    if (!showAvatarMenu) return;

    const handleClickOutside = (event) => {
      const avatarSection = event.target.closest('.avatar-section');
      if (!avatarSection) {
        closeAvatarMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAvatarMenu]);

  const handleLogout = () => {
    clearUser();
    window.location.href = '/login';
  };

  const handleThemeChange = (theme) => {
    setTheme(theme);
    // The useThemeEffect hook will automatically update the DOM
  };

  const openAvatarMenu = () => {
    // Открываем меню аватара
    setShowAvatarMenu(true);
  };
  
  const closeAvatarMenu = () => {
    setShowAvatarMenu(false);
  };
  
  const onPickAvatar = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      // Create FormData
      const formData = new FormData();
      formData.append('profileImage', file);
      
      // Update profile with new avatar
      const response = await userService.updateCurrentUserProfile(user.id || user._id, formData);
      const updatedUser = response?.data?.data || response?.data || response;
      
      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        await refreshUser();
        closeAvatarMenu();
        alert('Avatar updated successfully');
      }
      
      // Reset input to allow selecting the same file again
      if (event.target) {
        event.target.value = '';
      }
    } catch (error) {
      console.error('Error updating avatar:', error);
      alert(error?.response?.data?.message || error?.message || 'Failed to update avatar');
      
      // Reset input even on error
      if (event.target) {
        event.target.value = '';
      }
    }
  };
  
  const handleDeleteAvatar = async () => {
    if (!window.confirm('Are you sure you want to delete your avatar?')) {
      return;
    }
    
    try {
      // Send empty string to delete avatar
      const response = await userService.updateCurrentUserProfile(user.id || user._id, {
        profileImage: ''
      });
      const updatedUser = response?.data?.data || response?.data || response;
      
      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        await refreshUser();
        closeAvatarMenu();
        alert('Avatar deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting avatar:', error);
      alert(error?.response?.data?.message || error?.message || 'Failed to delete avatar');
    }
  };
  
  const openEditProfileModal = () => {
    // Reset form with current user data
    reset({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || '',
      password: '',
      confirmPassword: ''
    });
    setShowEditProfileModal(true);
  };
  
  const closeEditProfileModal = () => {
    setShowEditProfileModal(false);
    reset();
  };
  
  const onSubmitProfile = async (data) => {
    try {
      // Validate passwords match
      if (data.password && data.password !== data.confirmPassword) {
        alert('Passwords do not match');
        return;
      }
      
      // Prepare update data
      const updateData = {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        ...(data.password && { password: data.password })
      };
      
      const response = await userService.updateCurrentUserProfile(user.id || user._id, updateData);
      const updatedUser = response?.data?.data || response?.data || response;
      
      if (updatedUser) {
        localStorage.setItem('user', JSON.stringify(updatedUser));
        setUser(updatedUser);
        await refreshUser();
        closeEditProfileModal();
        alert('Profile updated successfully');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert(error?.response?.data?.message || error?.message || 'Failed to update profile');
    }
  };


  if (!isOpen) return null;

  return (
    <div className="user-toggle-settings">
      <div className="settings-overlay" onClick={onClose}></div>
      <div className="settings-dropdown">
        {/* User Info Section */}
        <div className="user-info-section">
          <AvatarSection
            avatarUrl={avatarUrl}
            showAvatarMenu={showAvatarMenu}
            onOpenMenu={openAvatarMenu}
            onPickAvatar={onPickAvatar}
            onDeleteAvatar={handleDeleteAvatar}
          />
          <div className="user-toggle-settings-details">
            <div className="user-toggle-settings-name">{displayName}</div>
            <div className="user-toggle-settings-email">{displayEmail}</div>
          </div>
        </div>

       

        {/* Theme Selection */}
        <div className="theme-selection">
         
          <div className="theme-buttons">
            <button 
              className={`theme-btn ${currentTheme === 'light' ? 'active' : ''}`}
              onClick={() => handleThemeChange('light')}
            >
              <img 
                src={SunIcon}
                alt="Light theme"
                style={{ width: '24px', height: '24px' }}
                className="theme-icon"
              />
            </button>
            <button 
              className={`theme-btn ${currentTheme === 'dark' ? 'active' : ''}`}
              onClick={() => handleThemeChange('dark')}
            >
              <img 
                src={DarkIcon}
                alt="Dark theme"
                style={{ width: '24px', height: '24px' }}
                className="theme-icon"
              />
            </button>
            <button 
              className={`theme-btn ${currentTheme === 'system' ? 'active' : ''}`}
              onClick={() => handleThemeChange('system')}
            >
              <img 
                src={SystemIcon}
                alt="System theme"
                style={{ width: '24px', height: '24px' }}
                className="theme-icon"
              />
            </button>
          </div>
        </div>

        {/* Settings and Logout Buttons */}
        <div className="logout-section">
          <button className="logout-btn" onClick={handleLogout}>
            <img 
              src={LogoutIcon}
              alt="Logout"
              style={{ width: '24px', height: '24px' }}
              className="logout-icon"
            />
            <span>Log out</span>
          </button>
          <button className="settings-btn" onClick={openEditProfileModal}>
            <img 
              src={SettingsIcon}
              alt="Settings"
              style={{ width: '24px', height: '24px' }}
              className="settings-icon"
            />
          </button>
        </div>
        
        {/* Edit Profile Modal */}
        {showEditProfileModal && (
          <div className="edit-profile-modal-overlay" onClick={closeEditProfileModal}>
            <div className="edit-profile-modal" onClick={(e) => e.stopPropagation()}>
              <div className="edit-profile-modal-header">
                <h2>Edit Profile</h2>
                <button className="close-btn" onClick={closeEditProfileModal}>×</button>
              </div>
              <form onSubmit={handleSubmit(onSubmitProfile)} className="edit-profile-form">
                <div className="form-grid">
                  <div className="form-field">
                    <label htmlFor="firstName" className="form-label">First Name</label>
                    <input
                      id="firstName"
                      className={`form-input ${errors.firstName ? "input-error" : ""}`}
                      placeholder="First Name"
                      {...register("firstName", { required: "First Name is required" })}
                    />
                    {errors.firstName && <p className="error-text">{errors.firstName.message}</p>}
                  </div>
                  
                  <div className="form-field">
                    <label htmlFor="lastName" className="form-label">Last Name</label>
                    <input
                      id="lastName"
                      className={`form-input ${errors.lastName ? "input-error" : ""}`}
                      placeholder="Last Name"
                      {...register("lastName", { required: "Last Name is required" })}
                    />
                    {errors.lastName && <p className="error-text">{errors.lastName.message}</p>}
                  </div>
                </div>
                
                <div className="form-field">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    id="email"
                    type="email"
                    className={`form-input ${errors.email ? "input-error" : ""}`}
                    placeholder="Email"
                    {...register("email", { 
                      required: "Email is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Invalid email format"
                      }
                    })}
                  />
                  {errors.email && <p className="error-text">{errors.email.message}</p>}
                </div>
                
                <div className="form-grid">
                  <div className="form-field password-field">
                    <label htmlFor="password" className="form-label">New Password</label>
                    <div className={`password-input-container ${errors.password ? "input-error" : ""}`}>
                      <input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        className="form-input"
                        placeholder="Leave empty to keep current password"
                        {...register("password", {
                          minLength: {
                            value: 6,
                            message: "Password must be at least 6 characters"
                          }
                        })}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="password-toggle"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff className="eye-icon" size={16} /> : <Eye className="eye-icon" size={16} />}
                      </button>
                    </div>
                    {errors.password && <p className="error-text">{errors.password.message}</p>}
                  </div>
                  
                  <div className="form-field confirm-password">
                    <label htmlFor="confirmPassword" className="form-label">Confirm Password</label>
                    <div className={`password-input-container ${errors.confirmPassword ? "input-error" : ""}`}>
                      <input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        className="form-input"
                        placeholder="Confirm new password"
                        {...register("confirmPassword", {
                          validate: (value) => {
                            const password = watch("password");
                            if (password && value !== password) {
                              return "Passwords do not match";
                            }
                            return true;
                          }
                        })}
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="password-toggle"
                        aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                      >
                        {showConfirmPassword ? <EyeOff className="eye-icon" size={16} /> : <Eye className="eye-icon" size={16} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <p className="error-text">{errors.confirmPassword.message}</p>}
                  </div>
                </div>
                
                <div className="edit-profile-modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeEditProfileModal}>
                    Cancel
                  </button>
                  <button type="submit" className="save-btn">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default UserToggleSettings;